import { MapElement } from '../../../types'
import { captureContentArea } from './boundingBoxHelpers'

export const captureCanvasScreenshot = (
  canvas: HTMLCanvasElement | null,
): string | null => {
  if (!canvas) return null

  try {
    // Get the canvas data as a data URL (base64 encoded image)
    // Using PNG format for better quality
    const dataUrl = canvas.toDataURL('image/png', 1.0)
    return dataUrl
  } catch (error) {
    console.error('Error capturing canvas screenshot:', error)
    return null
  }
}

export const captureCanvasThumbnail = (
  canvas: HTMLCanvasElement | null,
  maxWidth: number = 400,
  maxHeight: number = 300,
): string | null => {
  if (!canvas) return null

  try {
    // Create a temporary canvas for the thumbnail
    const tempCanvas = document.createElement('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    if (!tempCtx) return null

    // Calculate thumbnail dimensions while maintaining aspect ratio
    const aspectRatio = canvas.width / canvas.height
    let thumbWidth = maxWidth
    let thumbHeight = maxHeight

    if (aspectRatio > maxWidth / maxHeight) {
      thumbHeight = maxWidth / aspectRatio
    } else {
      thumbWidth = maxHeight * aspectRatio
    }

    // Set thumbnail canvas size
    tempCanvas.width = thumbWidth
    tempCanvas.height = thumbHeight

    // Set white background for better visibility
    tempCtx.fillStyle = 'white'
    tempCtx.fillRect(0, 0, thumbWidth, thumbHeight)

    // Draw the original canvas scaled down to thumbnail size
    tempCtx.drawImage(canvas, 0, 0, thumbWidth, thumbHeight)

    // Return thumbnail as data URL
    return tempCanvas.toDataURL('image/jpeg', 0.9) // Using JPEG with 90% quality for better clarity
  } catch (error) {
    console.error('Error capturing canvas thumbnail:', error)
    return null
  }
}

export const captureContentThumbnail = (
  canvas: HTMLCanvasElement | null,
  elements: MapElement[],
  zoom: number,
  panOffset: { x: number; y: number },
  maxWidth: number = 400,
  maxHeight: number = 300,
): string | null => {
  if (!canvas) return null

  // If no elements, fall back to regular canvas thumbnail
  if (elements.length === 0) {
    return captureCanvasThumbnail(canvas, maxWidth, maxHeight)
  }

  try {
    // First, get the content area canvas
    const contentCanvas = captureContentArea(canvas, elements, zoom, panOffset)
    if (!contentCanvas) return null

    // Create a thumbnail from the content canvas
    const tempCanvas = document.createElement('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    if (!tempCtx) return null

    // Calculate thumbnail dimensions while maintaining aspect ratio
    const aspectRatio = contentCanvas.width / contentCanvas.height
    let thumbWidth = maxWidth
    let thumbHeight = maxHeight

    if (aspectRatio > maxWidth / maxHeight) {
      thumbHeight = maxWidth / aspectRatio
    } else {
      thumbWidth = maxHeight * aspectRatio
    }

    // Set thumbnail canvas size
    tempCanvas.width = thumbWidth
    tempCanvas.height = thumbHeight

    // Set white background for better visibility
    tempCtx.fillStyle = 'white'
    tempCtx.fillRect(0, 0, thumbWidth, thumbHeight)

    // Draw the content canvas scaled down to thumbnail size
    tempCtx.drawImage(contentCanvas, 0, 0, thumbWidth, thumbHeight)

    // Return thumbnail as data URL
    return tempCanvas.toDataURL('image/jpeg', 0.9) // Using JPEG with 90% quality
  } catch (error) {
    console.error('Error capturing content thumbnail:', error)
    return null
  }
}
